import { AppLocalStorage } from "../app/managers/AppLocalStorage";
import { GTMTracking } from "../app/managers/GTMTracking";


export enum AudioPlayerEvents {
    MUSIC_ENABLED_CHANGE = "music_enabled_change",
}

export type AudioData = {
    /**
     * track key by which track is loaded in loader and by which is can be retrieved
     */
    key: string,
    /**
     * Urls of various formats of the same track (mp3, ogg..)
     */
    urls: string[],
    /**
     * Optional props for this audio file, like:
     * mute, volume, rate, detune, seek, loop, delay
     */
    soundConfig?: Phaser.Types.Sound.SoundConfig,
}

/**
 * Player of audio, with following responsibilities:
 * - play music and sound effects
 * - dynamic load/unload
 * - pause/resume on loss of context + iOS fix
 * - static (accessible anywhere, prevents context loss, duplicity of instantiation etc)
 * 
 * NOTE: Never ever use reference to AudioPlayer in this app. Use AppAudioPlayer instead!
 *       Reason: if you create anything using AppAudioPlayer call, that variable is unavailable via AudioPlayer ref, ex: AudioPlayer.IsMusicEnabled->undefined
 * 
 * @resources 
 *  @see https://blog.ourcade.co/posts/2020/phaser-3-web-audio-best-practices-games/
 *  @see https://labs.phaser.io/index.html?dir=audio/&q=
 */
export class AudioPlayer
{
    public static SCENE_KEY: string = "audio";
    /**
     * Playing or paused
     * @see curLoadedMusicTrack
     */
    public static curLoadedMusicTrackIndex: number = -1;
    /**
     * Loading (new)
     * - replaces (and becomes) @see curLoadedMusicTrack, once successfully loaded
     */
    public static curPendingMusicTrackIndex: number = -1;
    public static scene: Phaser.Scene;

    /**
     * Is music unmuted?
     */
    protected static isMusicEnabled: boolean;
    
    /**
     * Are sounds unmuted?
     */
    protected static isSoundsEnabled: boolean;
    protected static hasFocus: boolean;

    private static musicTrackDatas: AudioData[];
    /**
     * Playing or paused
     * @see curLoadedMusicTrackIndex
     */
    private static curLoadedMusicTrack: Phaser.Sound.BaseSound = null;

    private static DEBUG: boolean = false;
    protected static eventEmitter: Phaser.Events.EventEmitter;


    public static GetEmitter(): Phaser.Events.EventEmitter
    {
        return this.eventEmitter;
    }

    public static IsMusicEnabled():boolean
    {
        return this.isMusicEnabled;
    }

    /**
     * Mutes or unmutes music.
     * @param newEnabled 'not muted' (true) or 'muted' (false).
     * @param updateLocalStorage setting this to false allows to mute music, iwhout saving the changes
     *      use-case: temporary mute, eg 'flip screen advising user to flip device' will mute music temporarily
     */
    public static SetMusicEnabled(newEnabled:boolean, updateLocalStorage:boolean = true):void
    {
        // console.log("AudioPlayer::SetMusicEnabled; oldEnabled=",this.isMusicEnabled,"newEnabled=",newEnabled);
        // console.log(this.curLoadedMusicTrack);
        if (this.isMusicEnabled == newEnabled) {
            return;
        }

        if (this.curLoadedMusicTrack != null) {
            if (newEnabled) {
                if (this.hasFocus) {
                    this.curLoadedMusicTrack.resume();
                    // if (this.DEBUG) console.log("resume",this.curLoadedMusicTrack);
                    if (this.DEBUG) console.log("resume");
                }
            }
            else {
                if (this.curLoadedMusicTrack.isPlaying) {
                    this.curLoadedMusicTrack.pause();
                    // if (this.DEBUG) console.log("pause",this.curLoadedMusicTrack);
                    if (this.DEBUG) console.log("pause");
                }
            }
        }

        if (updateLocalStorage) {
            AppLocalStorage.SetMusicEnabled(newEnabled);
            // GTMTracking
            GTMTracking.PLDTrackingSettingChanged("music", newEnabled.toString());
        }
        this.isMusicEnabled = newEnabled;
    }

    /**
     * Only focused app plays music & sound
     * NOTE: apparently, this.audioScene.sound.mute {false|true} can not be used efficiently to just mute music when focus is lost; possibly after focus is lost for a long time and music is playing in the background, at some point it stops looping (playing). 
     */
    public static SetHasFocus(hasFocus: boolean): void
    {
        if (this.hasFocus == hasFocus) {
            return;
        }

        if (this.curLoadedMusicTrack != null) {
            if (this.curLoadedMusicTrack != null) {
                if (hasFocus) {
                    if (this.isMusicEnabled) {
                        this.curLoadedMusicTrack.resume();
                        // if (this.DEBUG) console.log("resume",this.curLoadedMusicTrack);
                        if (this.DEBUG) console.log("resume");
                    }
                }
                else {
                    if (this.curLoadedMusicTrack.isPlaying) {
                        this.curLoadedMusicTrack.pause();
                        // if (this.DEBUG) console.log("pause",this.curLoadedMusicTrack);
                        if (this.DEBUG) console.log("pause");
                    }
                }
            }
        }
        this.hasFocus = hasFocus;
    }

    /**
     * Mutes or unmutes sounds
     * @param newEnabled 'not muted' (true) or 'muted' (false).
     */
    public static SetSoundsEnabled(newEnabled:boolean):void
    {
        if (this.isSoundsEnabled != newEnabled) {
            // GTMTracking
            GTMTracking.PLDTrackingSettingChanged("sounds", newEnabled.toString());
            
            AppLocalStorage.SetSoundsEnabled(newEnabled);
            this.isSoundsEnabled = newEnabled;
        }
    }

    public static Init(scene:Phaser.Scene): void
    {
        this.scene = scene.scene.add(this.SCENE_KEY, AudioPlayerScene, true); // autostart?
        this.eventEmitter = new Phaser.Events.EventEmitter();

        this.isMusicEnabled = AppLocalStorage.GetMusicEnabled();
        this.isSoundsEnabled = AppLocalStorage.GetSoundsEnabled();
        // if we start with focus TRUE, we can play music straight off
        this.hasFocus = true;
    }

    /**
     * Cache music track data
     * Currently loaded music track (paused or playing) is then 
     * @see curLoadedMusicTrack which corresponds to @see curLoadedMusicTrackIndex
     * 
     * @param trackData 
     */
    public static AddMusicTrackData(trackData: AudioData): void
    {
        // console.log("AddMusicTrackData")
        if (this.musicTrackDatas == undefined) {
            this.musicTrackDatas = [];
        }
        this.musicTrackDatas.push(trackData);
    }

    /**
     * Return cached @see AudioData for music track by index
     * @param trackIndex 
     * @returns cached @see AudioData for music track by index
     */
    public static GetMusicTrackDataByIndex(trackIndex: number): AudioData
    {
        return this.musicTrackDatas[trackIndex];
    }

    /**
     * Loads and plays audio track
     * @param itemIndex 
     */
    public static PlayMusicTrackByIndex(itemIndex: number, enableMusicIfMuted: boolean = false): void
    {
        // if (this.DEBUG) console.log("AudioPlayer::PlayMusicTrackByIndex");
        // console.log(itemIndex, this.curLoadedMusicTrackIndex);

        // if (this.isMusicEnabled || onlyIfMusicEnabled) {
            if (itemIndex != this.curLoadedMusicTrackIndex) {
                // this.scene.sound.add(
                //     this.musicTrackDatas[itemIndex], 
                //     ["./audio/tracks/31127168_retro-latin-lounge_by_drtimwaters_preview.mp3"]);
                const audioData: AudioData = this.musicTrackDatas[itemIndex];

                // console.log(this.curLoadedMusicTrackIndex)

                if (this.curLoadedMusicTrackIndex != -1) {
                    // console.log("destroy:",this.curLoadedMusicTrackIndex)
                    const prevAudioData: AudioData = this.musicTrackDatas[this.curLoadedMusicTrackIndex];
                    this.scene.sound.removeByKey(prevAudioData.key);
                    this.curLoadedMusicTrack = null;
                }
                
                // we assume that music will start playing somewhere after this point
                // now if music was set to enable itself from 'muted' state
                if (enableMusicIfMuted) {
                    // and it is currently muted
                    if (!this.isMusicEnabled) {
                        // then enable music
                        this.SetMusicEnabled(true, true);
                        // and dispatch change in MUSIC_ENABLED, which will refresh appropriate secondary views
                        this.eventEmitter.emit(AudioPlayerEvents.MUSIC_ENABLED_CHANGE, this.isMusicEnabled);
                    }
                }

                // console.log(this.scene.sound, audioData.urls, this.curLoadedMusicTrack)
                this.curPendingMusicTrackIndex = itemIndex;
                this.scene.load.audio(audioData.key, audioData.urls);
                this.scene.load.once(Phaser.Loader.Events.COMPLETE, this.OnAudioTrackLoadComplete, this);
                if (this.DEBUG) console.log("loading track", itemIndex, audioData.key);
                this.scene.load.start();
                // console.log(audioData.key, audioData.urls, "isLoading?",this.scene.load.isLoading());
                // use https://photonstorm.github.io/phaser3-docs/Phaser.Loader.LoaderPlugin.html#reset__anchor 
                // to abort loading
            }
        // }
    }

    /**
     * sound.add or play() can optionally receive:
     * SoundConfig: https://photonstorm.github.io/phaser3-docs/Phaser.Types.Sound.html#.SoundConfig
     * > mute, volume, rate, detune, seek, loop, delay
     */
    private static OnAudioTrackLoadComplete(
        loader: Phaser.Loader.LoaderPlugin, 
        totalComplete: number,
        totalFailed: number
    ): void
    {
        if (this.DEBUG) console.log("AudioPlayer::OnAudioTrackLoadComplete");

        if (totalFailed == 0) {
            this.PlayLoadedAudioTrack(this.curPendingMusicTrackIndex);
        }
    }

    /**
     * Play loaded music track.
     * The way music tracks work is that the old track is removed, when new is called to play (swap)
     * @param trackIndex 
     */
    public static PlayLoadedAudioTrack(trackIndex: number): void
    {
        // console.trace("PlayLoadedAudioTrack", this.musicTrackDatas, trackIndex, this.musicTrackDatas[trackIndex])
        
        const trackData: AudioData = this.musicTrackDatas[trackIndex];
        if (trackData != undefined) {
            this.curLoadedMusicTrack = this.scene.sound.add(trackData.key, trackData.soundConfig);

            // Mobile devices require sounds to be triggered from an explicit user action, 
            // such as a tap, before any sound can be loaded/played on a web page. 
            // Set to true if the audio system is currently locked awaiting user interaction.
            if (!this.scene.sound.locked) {
                if (this.DEBUG) console.log("scene sound unlocked");
                // is unmuted and is focused
                if (this.isMusicEnabled && this.hasFocus) {
                    // play
                    this.curLoadedMusicTrack.play();
                    
                    if (this.DEBUG) console.log("playing");
                }
                // is muted or has not focus
                else {
                    // pause until unmuted
                    // NOTE: we need to call play() before pause(), otherwise there is no way to play() this loaded track later by simply calling resume() - BUG?
                    this.curLoadedMusicTrack.play();
                    this.curLoadedMusicTrack.pause();
                    if (this.DEBUG) console.log("pausing");
                }
                this.curPendingMusicTrackIndex = -1;
                this.curLoadedMusicTrackIndex = trackIndex;
            }
            else {
                if (this.DEBUG) console.log("waiting for scene sound unlock..");
                // wait for 'unlocked' to fire and then play
                this.scene.sound.once(Phaser.Sound.Events.UNLOCKED, () => {
                    if (this.DEBUG) console.log("unlocked");
                    // is unmuted and has focus
                    if (this.isMusicEnabled && this.hasFocus) {
                        // play
                        this.curLoadedMusicTrack.play();
                        if (this.DEBUG) console.log("playing");
                    }
                    // is muted or has not focus
                    else {
                        // console.log(this.isMusicEnabled, this.hasFocus);
                        
                        // pause until unmuted
                        // NOTE: we need to call play() before pause(), otherwise there is no way to play() this loaded track later by simply calling resume() - BUG?
                        this.curLoadedMusicTrack.play();
                        this.curLoadedMusicTrack.pause();
                        if (this.DEBUG) console.log("pausing");
                    }
                    // if (this.DEBUG) console.log("and the track is:", this.curLoadedMusicTrack);
                    this.curPendingMusicTrackIndex = -1;
                    this.curLoadedMusicTrackIndex = trackIndex;
                })
            }
        }
        else {
            if (this.DEBUG) console.log("AudioPlayer::trackData undefined!");
        }
    }
}

export class AudioPlayerScene extends Phaser.Scene {}